import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, UserCheck, Wallet, Trophy, MessageSquare, Package, LogOut, Menu, X, ExternalLink } from "lucide-react";
import { useAuth } from "@/components/site/auth";
import { Logo } from "@/components/site/Logo";

const NAV = [
  ["", "Dashboard", LayoutDashboard],
  ["students", "All Users", Users],
  ["approvals", "Approvals", UserCheck],
  ["payouts", "Payouts", Wallet],
  ["leaderboard", "Leaderboard", Trophy],
  ["packages", "Packages", Package],
  ["enquiries", "Leads", MessageSquare],
];

const Side = (close) => ([to, label, Icon]) => (
  <NavLink key={to} to={`/admin${to ? "/" + to : ""}`} end onClick={close} data-testid={`admin-nav-${to || "dashboard"}`}
    className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-[background-color,color,box-shadow] ${isActive ? "bg-[var(--pink)] text-[var(--on-accent)] shadow-[0_0_18px_-5px_var(--glow-pink)]" : "text-[var(--ink)] hover:bg-[var(--bg-2)]"}`}>
    <Icon size={17} /> {label}
  </NavLink>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const initials = user.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div data-testid="admin-layout" className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]">
      <aside className={`fixed lg:sticky top-0 z-50 h-screen w-[260px] glass !rounded-none !border-y-0 !border-l-0 flex flex-col p-4 transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex items-center justify-between px-1 pb-4 border-b border-[var(--surface-border)]">
          <Logo className="h-10" />
          <button onClick={close} className="lg:hidden text-[var(--muted)]"><X size={20} /></button>
        </div>
        <nav className="flex flex-col gap-1 mt-4 flex-1 overflow-y-auto">{NAV.map(Side(close))}</nav>
        <div className="border-t border-[var(--surface-border)] pt-3 mt-3">
          <a href="/" onClick={close} className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold text-[var(--muted)] hover:bg-[var(--bg-2)]"><ExternalLink size={17} /> View site</a>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold text-[var(--pink)] w-full"><LogOut size={17} /> Logout</button>
        </div>
      </aside>

      {open && <div onClick={close} className="fixed inset-0 z-40 bg-black/50 lg:hidden" />}

      <div className="min-w-0">
        <header className="glass !rounded-none !border-x-0 !border-t-0 sticky top-0 z-30 flex items-center justify-between px-5 py-3">
          <button onClick={() => setOpen(true)} className="lg:hidden glass w-10 h-10 grid place-items-center rounded-xl"><Menu size={18} /></button>
          <div className="hidden lg:block"><span className="eyebrow">Admin Panel</span></div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block"><p className="text-sm font-semibold leading-tight">{user.name}</p><p className="text-xs text-[var(--pink)]">Founder</p></div>
            <div className="w-10 h-10 rounded-full grid place-items-center font-bold text-[var(--on-accent)]" style={{ background: "var(--btn-bg)" }}>{initials}</div>
          </div>
        </header>
        <main className="p-5 sm:p-7 max-w-[1400px]"><Outlet /></main>
      </div>
    </div>
  );
}
